import React, { useEffect, useMemo, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { toast } from 'sonner';

import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api, { employeesApi, mediaApi } from '../lib/api';

function formatTimestamp(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function Screenshots() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('all');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [screenshots, setScreenshots] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState(null);
  const pageSize = 12;

  useEffect(() => {
    employeesApi
      .list({ page: 1, page_size: 200 })
      .then((response) => setEmployees(response.data?.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPage(1);
  }, [employeeId, date]);

  useEffect(() => {
    const loadScreenshots = async () => {
      if (!employees.length && employeeId === 'all') return;
      setLoading(true);
      setError('');
      try {
        const targets =
          employeeId === 'all'
            ? employees.map((employee) => ({ id: employee.id, name: employee.name }))
            : employees
                .filter((employee) => employee.id === employeeId)
                .map((employee) => ({ id: employee.id, name: employee.name }));

        const results = await Promise.all(
          targets.map(async (employee) => {
            const response = await mediaApi.screenshots(employee.id, date);
            return (response.data?.items || []).map((item) => ({
              ...item,
              employee_name: employee.name,
            }));
          })
        );

        setScreenshots(
          results.flat().sort((first, second) => new Date(second.ts) - new Date(first.ts))
        );
      } catch (err) {
        const message = err.response?.data?.detail || 'Failed to load screenshots.';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    loadScreenshots();
  }, [employeeId, date, employees]);

  useEffect(() => {
    screenshots.forEach((shot) => {
      if (imageUrls[shot.id]) return;
      api
        .get(shot.signed_view_url, { responseType: 'blob' })
        .then((response) => {
          setImageUrls((current) => ({
            ...current,
            [shot.id]: URL.createObjectURL(response.data),
          }));
        })
        .catch(() => {});
    });
  }, [screenshots, imageUrls]);

  const paginatedScreenshots = useMemo(
    () => screenshots.slice((page - 1) * pageSize, page * pageSize),
    [screenshots, page]
  );

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="screenshots-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Screenshots</h1>
        <p className="mt-2 text-slate-500">Review captured screenshots by employee and date.</p>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Screenshot Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="border-slate-600 bg-slate-700 text-white">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className="rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="card" />
          ) : paginatedScreenshots.length ? (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {paginatedScreenshots.map((shot) => (
                  <button
                    key={shot.id}
                    type="button"
                    className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800 text-left shadow-lg transition hover:border-blue-500/40"
                    onClick={() => setLightbox(shot)}
                  >
                    <div className="aspect-video bg-slate-900">
                      {imageUrls[shot.id] ? (
                        <img alt="" className="h-full w-full object-cover" src={imageUrls[shot.id]} />
                      ) : (
                        <div className="flex h-full items-center justify-center text-slate-500">
                          <Camera className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm text-white">{shot.employee_name}</p>
                      <p className="mt-1 text-xs text-slate-500">{formatTimestamp(shot.ts)}</p>
                    </div>
                  </button>
                ))}
              </div>
              {screenshots.length > pageSize ? (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    Page {page} of {Math.max(1, Math.ceil(screenshots.length / pageSize))}
                  </p>
                  <div className="flex gap-2">
                    <button
                      className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-300 disabled:opacity-40"
                      disabled={page <= 1}
                      onClick={() => setPage((current) => current - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-300 disabled:opacity-40"
                      disabled={page >= Math.ceil(screenshots.length / pageSize)}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <EmptyState
              icon={<Camera className="h-8 w-8" />}
              title="No screenshots"
              description="No screenshots were captured for the selected employee and date."
            />
          )}
        </CardContent>
      </Card>

      {lightbox ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6">
          <button className="absolute right-6 top-6 text-white" onClick={() => setLightbox(null)}>
            <X className="h-8 w-8" />
          </button>
          <div className="max-w-6xl">
            <img alt="" className="max-h-[80vh] rounded-lg" src={imageUrls[lightbox.id]} />
            <p className="mt-4 text-center text-sm text-slate-300">
              {lightbox.employee_name} • {formatTimestamp(lightbox.ts)}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
